import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import BookingSearchSection from './BookingSearchSection';
import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';
import { InvestorHeroSection, TrustSection } from './InvestorHomepageSections';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const homepageCopy = {
  'I Need a Makeup Artist': 'I Need a Makeup Artist',
  'I Am a Makeup Artist': 'I’m a Makeup Artist',
  'Find available makeup artists near you and get booked fast.':
    'Browse Sydney makeup artists, compare prices and request a booking in one place.',
  'Join to get discovered, fill empty slots, and grow your bookings.':
    'Get discovered by Sydney clients, promote open appointment times and grow your bookings.',
  'Search Makeup Artists': 'View Sydney Artists',
};

const updateHomepageCopy = value => {
  if (typeof value === 'string') {
    return homepageCopy[value] || value;
  } else if (Array.isArray(value)) {
    return value.map(updateHomepageCopy);
  } else if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, updateHomepageCopy(nestedValue)])
    );
  }
  return value;
};

const investorSections = {
  hero: {
    sectionType: 'investorHero',
    sectionId: 'find-your-artist',
    sectionName: 'Hotline Glam hero',
  },
  bookingSearch: {
    sectionType: 'bookingSearch',
    sectionId: 'choose-date-and-time',
    sectionName: 'Choose date and preferred time',
  },
  trust: {
    sectionType: 'trustSignals',
    sectionId: 'why-hotline-glam',
    sectionName: 'Trust signals',
  },
};

const createInvestorReadyHomepage = landingPageData => {
  if (!landingPageData) {
    return landingPageData;
  }

  const allSections = Array.isArray(landingPageData.sections) ? landingPageData.sections : [];
  const originalHero = allSections.find(section => section?.sectionType === 'hero');
  const listingSections = allSections.filter(section => section?.sectionType === 'listings');

  const heroWithOriginalBanner = {
    ...investorSections.hero,
    appearance: originalHero?.appearance,
  };
  const trustWithOriginalBanner = {
    ...investorSections.trust,
    appearance: originalHero?.appearance,
  };

  return {
    ...landingPageData,
    sections: [
      heroWithOriginalBanner,
      investorSections.bookingSearch,
      ...listingSections,
      trustWithOriginalBanner,
    ],
  };
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;
  const landingPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const investorReadyHomepage = updateHomepageCopy(createInvestorReadyHomepage(landingPageData));

  return (
    <PageBuilder
      pageAssetsData={investorReadyHomepage}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
      options={{
        sectionComponents: {
          investorHero: { component: InvestorHeroSection },
          bookingSearch: { component: BookingSearchSection },
          trustSignals: { component: TrustSection },
        },
      }}
      notice={
        <>
          <strong>Now live across Sydney.</strong> More Australian cities are coming soon.
        </>
      }
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
